// Zero-knowledge escrow of the user's PQC private keys, for multi-device use.
//
// The private keys are wrapped IN THE BROWSER with a key derived from a separate
// encryption passphrase (PBKDF2-SHA256 -> AES-256-GCM via WebCrypto). Only the
// resulting ciphertext is uploaded to kmapi (/auth/v1/keybackup). The server
// never sees the passphrase or the plaintext keys, so it cannot read the user's
// mail; it just hands the encrypted bundle back so any device can unlock the SAME
// keypair with the passphrase. No key rotation, full history stays decryptable.

const PBKDF2_ITERATIONS = 600000; // ~a few hundred ms in-browser; resists offline guessing
const enc = new TextEncoder();
const dec = new TextDecoder();

const toB64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const fromB64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

export function cryptoAvailable() {
  return typeof crypto !== 'undefined' && !!crypto.subtle && !!crypto.getRandomValues;
}

async function deriveKey(passphrase, salt, iterations) {
  const base = await crypto.subtle.importKey('raw', enc.encode(passphrase), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

// Wrap { kem, sig } private keys with a passphrase -> opaque server bundle.
export async function wrapKeys(privateKeys, passphrase) {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(passphrase, salt, PBKDF2_ITERATIONS);
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(JSON.stringify(privateKeys)));
  return { v: 1, kdf: 'PBKDF2-SHA256', iterations: PBKDF2_ITERATIONS, salt: toB64(salt), iv: toB64(iv), wrapped: toB64(ct) };
}

// Unwrap a server bundle with a passphrase -> { kem, sig }. Throws on a wrong
// passphrase (AES-GCM auth-tag mismatch) or a corrupt bundle.
export async function unwrapKeys(bundle, passphrase) {
  const key = await deriveKey(passphrase, fromB64(bundle.salt), bundle.iterations || PBKDF2_ITERATIONS);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: fromB64(bundle.iv) }, key, fromB64(bundle.wrapped));
  return JSON.parse(dec.decode(pt));
}

// Whether this account already has an escrow bundle (authed). Lets a device that
// holds local keys decide if it still needs to upload a backup for other devices.
export async function status(authHeader) {
  const res = await fetch('/auth/v1/keybackup/status', { headers: { Authorization: authHeader } });
  if (!res.ok) throw new Error(`keybackup status failed: ${res.status}`);
  return res.json(); // { exists, updatedAt }
}

// Fetch this account's encrypted bundle (authed). null if none exists yet.
export async function fetchBackup(authHeader) {
  const res = await fetch('/auth/v1/keybackup', { headers: { Authorization: authHeader } });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`keybackup fetch failed: ${res.status}`);
  return res.json();
}

// Upload/replace the encrypted bundle (authed). `pubFingerprint` is public-only
// metadata so the server/UI can tell which keypair the bundle belongs to.
export async function putBackup(authHeader, bundle, pubFingerprint) {
  const res = await fetch('/auth/v1/keybackup', {
    method: 'PUT',
    headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...bundle, pubFingerprint: pubFingerprint || null }),
  });
  if (!res.ok) throw new Error(`keybackup save failed: ${res.status}`);
  return res.json();
}
